import {getTestProps} from '@benchling/js-test-utils';
import React, {useMemo} from 'react';

import {assert} from 'Asserts';
import {ProcessFlowchartEdgeType} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorConstants';
import {isInteractiveTaskNode} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorNodeTypes';

import {FieldDescription} from './transition-field-info/FieldDescription';
import {useStyles} from './transition-field-info/transitionFieldInfoStyles';
import type {TransitionFieldInfoSectionProps} from './transition-field-info/TransitionMappingsTable';
import {
  InboundMappingsTable,
  OutboundMappingsTable,
} from './transition-field-info/TransitionMappingsTableForMultipleConnections';
import {useFieldEditor} from './transition-field-info/useFieldEditor';

export default function TransitionFieldInfoSection(props: TransitionFieldInfoSectionProps): JSX.Element {
  const {classes} = useStyles();
  const selectedField = useMemo(() => {
    assert(isInteractiveTaskNode(props.node), 'Expected the selected node to be an interactive task node', {
      node: props.node,
    });

    for (const section of props.node.data.sections) {
      for (const row of section.sectionRows) {
        if (row.clientId === props.selectedFieldId) {
          return row;
        }
      }
    }

    assert(
      false,
      `Expected to find the selected field ${props.selectedFieldId} in the selected node ${props.node.data.nodeName}`,
      {
        selectedFieldId: props.selectedFieldId,
        nodeId: props.node,
      }
    );
  }, [props.selectedFieldId, props.node]);

  const {schemaField, availableFieldOptions, schemaFieldSelectors, handleFieldUpdate, taskDefinitionId} =
    useFieldEditor(props, selectedField);

  // The selected node is the source if there is a control flow edge from the selected node to another node.
  // Otherwise the node is a "target" node.
  const nodeIsTarget = props.flowchart.edges.some(
    (edge) =>
      edge.type === ProcessFlowchartEdgeType.CONTROL_FLOW &&
      edge.target === props.node.id &&
      edge.targetHandle != null
  );

  return (
    <div className={classes.container}>
      <FieldDescription
        key={selectedField.clientId}
        selectedField={selectedField}
        nodeName={props.node.data.nodeName}
        onClickTask={() => {
          props.onChangeSelectedFieldId(null);
        }}
        schemaField={schemaField}
        onFieldUpdate={handleFieldUpdate}
        availableFieldOptions={availableFieldOptions}
        schemaFieldSelectors={schemaFieldSelectors}
        locked={props.locked || taskDefinitionId == null}
      />

      {nodeIsTarget && (
        <div {...getTestProps({component: 'TransitionFieldInfoSection', element: 'inbound-mappings'})}>
          <div className={classes.mappingSectionTitle}>Inbound mappings</div>
          <InboundMappingsTable {...props} />
        </div>
      )}
      <div {...getTestProps({component: 'TransitionFieldInfoSection', element: 'outbound-mappings'})}>
        <div className={classes.mappingSectionTitle}>Outbound mappings</div>
        <OutboundMappingsTable {...props} />
      </div>
    </div>
  );
}
